/**
 * Holiday detection for the TalentPop "Add more agents" button.
 *
 * The button surfaces in the 21-day run-up to a major retail holiday so
 * merchants can staff up before the surge. This module is the pure
 * date-math half of that trigger — no React, no data fetching.
 */

import { nthWeekdayOfMonth, TemporalDayOfWeek } from "@redotech/util/temporal";

export const TALENTPOP_HOLIDAY_WINDOW_DAYS = 21;

export type HolidayKey =
  | "valentines-day"
  | "mothers-day"
  | "july-4th"
  | "labor-day"
  | "black-friday"
  | "christmas";

interface Holiday {
  readonly key: HolidayKey;
  /** The holiday's date in a given calendar year. */
  readonly dateForYear: (year: number) => Temporal.PlainDate;
}

const HOLIDAYS: readonly Holiday[] = [
  {
    key: "valentines-day",
    dateForYear: (year) => Temporal.PlainDate.from({ year, month: 2, day: 14 }),
  },
  {
    // 2nd Sunday of May
    key: "mothers-day",
    dateForYear: (year) =>
      nthWeekdayOfMonth(year, 5, TemporalDayOfWeek.SUNDAY, 2),
  },
  {
    key: "july-4th",
    dateForYear: (year) => Temporal.PlainDate.from({ year, month: 7, day: 4 }),
  },
  {
    // 1st Monday of September
    key: "labor-day",
    dateForYear: (year) =>
      nthWeekdayOfMonth(year, 9, TemporalDayOfWeek.MONDAY, 1),
  },
  {
    // Day after the 4th Thursday of November
    key: "black-friday",
    dateForYear: (year) =>
      nthWeekdayOfMonth(year, 11, TemporalDayOfWeek.THURSDAY, 4).add({
        days: 1,
      }),
  },
  {
    key: "christmas",
    dateForYear: (year) =>
      Temporal.PlainDate.from({ year, month: 12, day: 25 }),
  },
];

/**
 * The holiday whose run-up window contains `today`, or `null` when no
 * holiday is within range. The window opens 21 days before the holiday and
 * closes at the end of the day after the holiday so the button stays visible
 * for ~24 hours past the holiday itself. Holiday windows do not overlap, so
 * at most one holiday can match per call. The TalentPop holiday trigger
 * fires exactly when this returns non-null.
 */
export function getActiveHoliday(today: Temporal.PlainDate): HolidayKey | null {
  for (const holiday of HOLIDAYS) {
    const thisYearDate = holiday.dateForYear(today.year);
    // If this year's window has already closed, look at next year's.
    const dayAfterThisYear = thisYearDate.add({ days: 1 });
    const occurrence =
      Temporal.PlainDate.compare(today, dayAfterThisYear) > 0
        ? holiday.dateForYear(today.year + 1)
        : thisYearDate;
    const windowStart = occurrence.subtract({
      days: TALENTPOP_HOLIDAY_WINDOW_DAYS,
    });
    const windowEnd = occurrence.add({ days: 1 });
    if (
      Temporal.PlainDate.compare(today, windowStart) >= 0 &&
      Temporal.PlainDate.compare(today, windowEnd) <= 0
    ) {
      return holiday.key;
    }
  }
  return null;
}
